import {
  Button,
  EButtonColorVariant,
  EHelpTextVariant,
  ELinkButtonVariant,
  ETextInputTypes,
  HelpText,
  LinkButton,
  TextInput,
} from '@outdoorsyco/bonfire';
import React, { MutableRefObject, useCallback, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { FORGOT_PAGE } from '@/constants/urls';
import { emailValidator, passwordValidator } from '@/constants/validationRules';
import { ILoginForm } from '@/services/types/auth/ILoginForm';

import { TFieldErrors } from './types/TFieldErrors';

export interface IProps {
  isModal?: boolean;
  onLogin: (credentials: ILoginForm) => void;
  isLoading?: boolean;
  error?: { error?: string | undefined } | string | null;
  onForgotPassword?: () => void;
  email?: string;
  reCaptchaToken: string;
}

const LoginForm: React.FC<IProps> = ({ onLogin, reCaptchaToken, ...props }) => {
  const recaptchaRef: MutableRefObject<ReCAPTCHA | null> = useRef(null);
  const shouldUseRecaptcha = !!reCaptchaToken;

  //#region INTL
  const intl = useIntl();
  const email = intl.formatMessage({ defaultMessage: 'Email address', id: 'hJZwTS' });
  const password = intl.formatMessage({ defaultMessage: 'Password', id: '5sg7KC' });
  const btnLabelLogin = intl.formatMessage({ defaultMessage: 'Log in', id: 'odXlk8' });
  const linkLabelForgot = intl.formatMessage({
    defaultMessage: 'Forgot your password?',
    id: 'cyRU1N',
  });
  const labelLoginFormHeader = intl.formatMessage({
    defaultMessage: 'Log in with your email',
    id: 's/9dk9',
    description: 'Login Modal Title',
  });
  const showPasswordLabel = intl.formatMessage({
    defaultMessage: 'Show password',
    id: 'JHTwdB',
  });
  //#endregion

  //#region Form Validation & Submit
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ILoginForm>();

  const onSubmit = useCallback(
    async (formValues: ILoginForm) => {
      const token = (shouldUseRecaptcha && (await recaptchaRef.current?.executeAsync())) || '';
      const data = { ...formValues, recaptcha_response: token };
      await onLogin(data);
      recaptchaRef.current?.reset();
    },
    [onLogin, shouldUseRecaptcha],
  );

  const fieldErrors: TFieldErrors<keyof ILoginForm> = {
    email: {
      required: intl.formatMessage({
        defaultMessage: 'Email is required',
        id: 'cU/aSG',
      }),
      pattern: intl.formatMessage({
        defaultMessage: 'Please enter a valid email address.',
        id: 'xs/MJu',
      }),
    },
    password: {
      required: intl.formatMessage({
        defaultMessage: 'Password is required.',
        id: 'pRvgsc',
      }),
    },
  };

  const emailValue = watch('email');
  const passwordValue = watch('password');
  const fieldsAreEmpty = !emailValue || !passwordValue;

  const emailErr = errors.email && fieldErrors.email[errors.email.type];
  const passErr = errors.password && fieldErrors.password[errors.password.type];

  return (
    <>
      {!props.isLoading && props.error && (
        <HelpText
          className="mb-2"
          text={typeof props.error === 'string' ? props.error : props.error.error || ''}
          variant={EHelpTextVariant.Error}
          showIcon
        />
      )}
      {!props.isModal && <div className="mb-2">{labelLoginFormHeader}</div>}
      <form className="flex flex-col w-full pt-2 pb-2 bg-white" onSubmit={handleSubmit(onSubmit)}>
        <div className={emailErr ? 'mb-8' : 'mb-4'}>
          <Controller
            name="email"
            control={control}
            rules={emailValidator}
            defaultValue={props.email}
            render={({ field }) => (
              <TextInput
                controlled
                name="email"
                autoComplete="email"
                label={email}
                ariaLabel={email}
                inputValue={field.value || ''}
                onChange={field.onChange}
                error={!!emailErr}
                helpText={emailErr}
              />
            )}
          />
        </div>

        <div className={passErr ? 'mb-8' : 'mb-4'}>
          <Controller
            name="password"
            control={control}
            rules={passwordValidator}
            render={({ field }) => (
              <TextInput
                controlled
                name="password"
                autoComplete="current-password"
                label={password}
                ariaLabel={password}
                inputValue={field.value || ''}
                onChange={field.onChange}
                error={!!passErr}
                helpText={passErr}
                type={ETextInputTypes.Password}
                showPasswordButtonAriaLabel={showPasswordLabel}
              />
            )}
          />
        </div>

        <div className="flex items-center mt-6 space-y-0 space-x-6">
          <Button
            className="w-full flex-2"
            variant={EButtonColorVariant.Primary}
            label={btnLabelLogin}
            loading={props.isLoading}
            disabled={props.isLoading || fieldsAreEmpty}
            type="submit"
          />
          {props.isModal ? (
            <LinkButton
              className="justify-center flex-1 w-full"
              variant={ELinkButtonVariant.Underline}
              label={linkLabelForgot}
              onClick={() => {
                if (props.onForgotPassword) {
                  props.onForgotPassword();
                }
              }}
            />
          ) : (
            <LinkButton
              className="justify-center flex-1 w-full"
              variant={ELinkButtonVariant.Underline}
              label={linkLabelForgot}
              href={FORGOT_PAGE}
            />
          )}
        </div>
        {shouldUseRecaptcha && (
          <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={reCaptchaToken} />
        )}
      </form>
    </>
  );
};

export { LoginForm };
